import React, { useState, useEffect, createContext, useMemo, useContext } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import _ from 'lodash';
import {
  Button,
  Grid,
  Tabs,
  Tab,
  InputLabel,
  Typography,
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  TextField,
  IconButton,
  Checkbox,
  FormControlLabel,
} from '@material-ui/core';
import {
  Save as SaveIcon,
  MoreVert as MoreVertIcon,
  Lock as LockIcon,
} from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { useParams, useHistory } from 'react-router-dom';

import * as estimateSummaryService from 'services/estimateSummaries';
import * as projectService from 'services/projects';
import { Dashboard as DashboardLayout } from 'layouts';
import { FormField2 as Field, CommonPanel, NormVersionPicker, MenuCustom, TrialHandler, CommonDialog, AutoComplete, LicenceRequestForm, canAccess as canAccessUtil } from 'components';
import { timestampToDate, toast, twoOrMoreDecimals, scrollToElement, setElementHighlight, blobToFileDownload, getQueryVariable, getCurrentAccount } from 'utils/helpers';
import api from 'utils/api';
import { EDU_MODE } from 'utils/config';
import EstimateSummaryTable from './EstimateSummaryTable';
import EstimateSummarySettingsModal from './EstimateSummarySettingsModal';
import EstimateSummaryCalculationSettingsModal from './EstimateSummaryCalculationSettingsModal';
import EstimateObjectImportModal from './EstimateObjectImportModal';
import EstimateSummaryPrintModal from './EstimateSummaryPrintModal';
import EstimateSummaryTransferModal from './EstimateSummaryTransferModal';

import './EstimateSummary.css';

const SERVICE_CODE = 'estimates.summary';
const SERVICE_CODE_DB = 'SAM';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: '15px',
    padding: theme.spacing(4),
    // maxWidth: '1200px',
  },
  titleCoefficients: {
    fontSize: '9px',
    padding: '0px 8px 2px',
  },
}));

const EstimateSummary = (props) => {
  const { projectId, summaryId } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const [record, setRecord] = useState(null);
  const [project, setProject] = useState(null);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [objectModalOpenForItem, setObjectModalOpenForItem] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [calculationSettingsModalOpen, setCalculationSettingsModalOpen] = useState(false);
  const [canAccessService, setCanAccessService] = useState(false);
  const [config, setConfig] = useState(null);
  const [isTrial, setIsTrial] = useState(!!getQueryVariable('trial'));
  const [showTrialMessage, setShowTrialMessage] = useState(getCurrentAccount().trialServices.includes(SERVICE_CODE));
  const [showRowOrderArrows, setShowRowOrderArrows] = useState(false);
  const { t } = useTranslation();
  const isNew = !record?._id;

  const fetchProject = async (projectId) => {
    try {
      const data = await projectService.show(projectId);
      setProject(data);
    } catch (err) {
      console.error(err);
      toast(t('Klaida įkeliant įrašą'), 'error');
    }
  }

  const fetchConfig = async () => {
    try {
      const data = await estimateSummaryService.getConfig();
      setConfig(data);
    } catch (err) {
      console.error(err);
      toast(t('Klaida įkeliant konfigūraciją'), 'error');
    }
  }

  const checkLicence = async () => {
    try {
      const response = await api.post(`/licences/licence-valid`, { service: SERVICE_CODE_DB, rule: SERVICE_CODE });
      setCanAccessService(response.data.validLicence);
      return response.data.validLicence;
    } catch (err) {
      console.error(err);
      toast(t('Klaida įkeliant konfigūraciją'), 'error');
    }
  }

  const fetchRecord = async (id) => {
    try {
      const data = await estimateSummaryService.show(id, true);
      setRecord(data);
    } catch (err) {
      console.error(err);
      toast(t('Klaida įkeliant įrašą'), 'error');
    }
  }

  useEffect(() => {
    if (isTrial) return;

    if (!record?._id && summaryId) fetchRecord(summaryId);
    checkLicence().then(canAccess => {
      if (canAccess) fetchProject(projectId)
        .then(() => fetchConfig())
    });
  }, []);

  useEffect(() => {
    if (record == null && project?.title) {
      setRecord({ ...record, title: project?.title });
    }
  }, [project])

  const handleRecordChange = ({ name, value }, submitAfter = false) => {
    const newRecord = {
      ...record,
      [name]: value,
    };

    setRecord(newRecord);

    if (submitAfter) handleSubmit(newRecord, submitAfter);
  }

  const handleSummaryDelete = async () => {
    if (!window.confirm(t('Ar tikrai norite pašalinti?'))) return;

    try {
      await estimateSummaryService.remove(summaryId);

      toast(t('Suvestinis skaičiavimas pašalintas'), 'success');
      history.push(`/projects/${projectId}/dashboard`);
    } catch (err) {
      console.error(err);
      toast(t('Nepavyko pašalinti'), 'error');
    }
  };

  const handleRecalculate = async () => {
    try {
      await estimateSummaryService.recalculate(summaryId);

      fetchRecord(summaryId);
      toast(t('Perskaičiuota'), 'success');
    } catch (err) {
      console.error(err);
      toast(t('Nepavyko perskaičiuoti'), 'error');
    }
  };

  const handleClone = async () => {
    try {
      const { _id } = await estimateSummaryService.clone(summaryId);

      toast(t('Suvestinis skaičiavimas nukopijuotas'), 'success');
      history.push(`/projects/${projectId}/summaries/${_id}`);
      await fetchRecord(_id);
    } catch (err) {
      console.error(err);
      toast(t('Nepavyko kopijuoti'), 'error');
    }
  }

  const handleSubmit = async (data = null, hideToast = false) => {
    try {
      const payload = _.omit(data || record, 'items');
      payload.project = projectId;

      if (!summaryId) {
        if (!payload?.title) payload.title = project?.title;
        const { _id } = await estimateSummaryService.create(payload);

        if (!hideToast) toast(t('Suvestinis skaičiavimas sukurtas'), 'success');
        history.replace(`/projects/${projectId}/summaries/${_id}`);
      } else {
        await estimateSummaryService.update(summaryId, payload);

        await fetchRecord(record._id);
        if (!hideToast) toast(t('Atnaujinta'), 'success');
      }
    } catch (err) {
      console.error(err);
      toast(t('Nepavyko išsaugoti'), 'error');
    }
  };

  const handleReadonly = async (isReadonly) => {
    if (!window.confirm(t(`Ar tikrai norite ${!isReadonly ? 'leisti' : 'neleisti'} redaguoti šį skaičiavimą?`))) return;

    try {
      const { data } = await api.post(`/estimate-summaries/${summaryId}/readonly`, { isReadonly });

      toast(t('Atnaujinta'), 'success');

      await fetchRecord(record._id);
    } catch (err) {
      console.error(err);
      toast(t('Nepavyko suformuoti'), 'error');
    }
  }

  const handleArchive = async (isArchived) => {
    if (!window.confirm(t(`Ar tikrai norite ${isArchived ? 'archyvuoti' : 'iškelti iš archyvo'} šį skaičiavimų objektą?`))) return;

    try {
      const { data } = await api.post(`/estimate-summaries/${summaryId}/archive`, { isArchived });

      toast(t('Atnaujinta'), 'success');

      if (isArchived == true) {
        history.push(`/projects/${projectId}/dashboard`);
      } else await fetchRecord(record._id);
    } catch (err) {
      console.error(err);
      toast(t('Nepavyko suformuoti'), 'error');
    }
  }

  const handleObjectSearch = (item) => {
    setObjectModalOpenForItem({ ...item, isEstimateObject: true });
  };

  const handleSettingsModalClose = (shouldFetchRecord = false) => {
    setSettingsModalOpen(false);

    if (shouldFetchRecord) fetchRecord(summaryId);
  };

  const handleObjectEdit = (estimateObjectId, inNewWindow = false) => {
    const estimateObjectUrl = `/projects/${projectId}/objects/${estimateObjectId}`;

    if (!inNewWindow) {
      history.push(estimateObjectUrl);
    } else {
      window.open(estimateObjectUrl, '_blank');
    }
  };

  const handleRecoveryPriceEdit = (recoveryPriceId, inNewWindow = false) => {
    const recoveryPriceUrl = `/recovery-price/${recoveryPriceId}`;

    if (!inNewWindow) {
      history.push(recoveryPriceUrl);
    } else {
      window.open(recoveryPriceUrl, '_blank');
    }
  };

  const handleObjectImportSelected = async (objectId, shouldMatchVersion = false, isEstimateObject = true, order) => {
    try {
      const payload = isEstimateObject ? {
        parent: objectModalOpenForItem.parent,
        estimateObject: objectId,
        order: order,
      } : {
        parent: objectModalOpenForItem.parent,
        recoveryPrice: objectId,
        order: order,
      };

      if (shouldMatchVersion) {
        payload.matchVersion = true;
      }

      await api.post(`/estimate-summaries/${summaryId}/items`, payload);

      toast(t('Pridėta'), 'success');

      fetchRecord(summaryId);
    } catch (err) {
      console.error(err);

      toast(t('Nepavyko pridėti'), 'error');
    }

    setObjectModalOpenForItem(null);
  }

  const renderTrial = () => {

    return (
      <TrialHandler
        service="estimates.summary"
      />
    )
  }

  const renderTrialMessage = () => {

    return EDU_MODE ? null : (
      <div style={{ margin: 10 }}>
        <Alert
          variant="filled"
          severity="info"
        // onClose={() => this.setState({ showTrialMessage: false })}
        >
          {t('Jūs naudojate bandomąją (laikinai nemokamą) paslaugos versiją.')}
        </Alert>
      </div>
    )
  }

  const renderLicenceRequest = () => {

    return (
      <LicenceRequestForm
        serviceCode={SERVICE_CODE}
      />
    )
  }

  const handleValueRound = (value) => {
    if (value === null || value === undefined) return value;

    if ([0, 1, 2, 3].includes(record?.settings?.numbersAfterDecimal)) {
      return parseFloat(value).toFixed(Math.abs(record.settings.numbersAfterDecimal));
    } else {
      return twoOrMoreDecimals(value);
    }
  }

  return (
    <>
      <DashboardLayout
        titleComponent={!isNew &&
          <Grid container spacing={2}>
            <Grid item style={{ width: '260px' }}>
              <div style={{ display: 'flex' }}>
                <Field
                  name="title"
                  onChange={handleRecordChange}
                  value={record?.title}
                  label={t('Skaičiavimų objekto pavadinimas')}
                  labelColor="#0767DB"
                  shrink
                  style={{ width: 220 }}
                  disabled={record?.isReadonly}
                />
                <IconButton
                  color="primary"
                  onClick={() => handleSubmit()}
                  style={{ marginTop: 10 }}
                  size="small"
                  disableFocusRipple={true}
                  disableRipple={true}
                  disabled={record?.isReadonly}
                >
                  <SaveIcon style={{ width: 28, height: 28 }} />
                </IconButton>
              </div>
            </Grid>
            <Grid item style={{ paddingLeft: 32 }}>
              <NormVersionPicker
                value={record?.version?.code}
                name="version"
                onChange={({ value }) => handleRecordChange({ name: 'version', value }, true)}
                label={t('Kainyno versija')}
                labelColor="#0767DB"
                style={{ marginTop: 16, width: '180px' }}
                type="calculations"
                variant="estimates"
                allowBlank
                disabled={record?.isReadonly}
              />
            </Grid>
          </Grid>
        }
        breadcrumbs={[
          { title: t('Projektai'), to: '/projects/' },
          { title: project?.title || '-', to: `/projects/${projectId}/dashboard` },
          { title: t('Suvestiniai skaičiavimai') },
          {
            title: `${!summaryId ? t('Naujas skaičiavimas') : (record?.title || '-')} ${record?.version?.version ? '(' + t('Kainynas ') + ' ' + record?.version?.version + ')' : ''}`,
            style: {
              whiteSpace: 'pre-wrap',
            }
          },
        ]}
        breadcrumbsObject={!isNew && (
          <div>
            <div style={{ paddingRight: 12, marginTop: 20 }}>
              
              {!!record?.isReadonly ? (
                <div style={{
                  display: 'inline-block',
                  padding: '6px 10px',
                  backgroundColor: '#2196F3',
                  borderRadius: '4px',
                  color: '#fff',
                  marginRight: '10px',
                  verticalAlign: 'middle',
                }}>
                  <LockIcon style={{ verticalAlign: 'text-top' }} /> {t('Peržiūros rėžimas')}
                </div>
              ) :
                <FormControlLabel
                  control={<Checkbox checked={showRowOrderArrows}
                    onChange={() => setShowRowOrderArrows(!showRowOrderArrows)}
                    name="showRowOrderArrowsCheckbox" />}
                  label="Keisti eilučių išdėstymą?"
                  style={{ paddingLeft: 20 }}
                />
              }
              <Button
                variant="contained"
                color="primary"
                onClick={() => setPrintModalOpen(true)}
                style={{ marginRight: 10 }}
              >
                {t('Spausdinti')}
              </Button>

              <MenuCustom
                buttonText={t('Veiksmai')}
                endIcon={<MoreVertIcon />}
                buttonVariant="contained"
                buttonColor="primary"
                items={!record.isReadonly ? [
                  // { label: t('Nustatymai'), handler: () => setSettingsModalOpen(true) },
                  { label: t('Išlaidų nustatymai'), handler: () => setSettingsModalOpen(true) },
                  { label: t('Skaičiavimų nustatymai'), handler: () => setCalculationSettingsModalOpen(true) },
                  { label: t('Perskaičiuoti'), handler: handleRecalculate },
                  { label: t('Kopijuoti'), handler: handleClone },
                  { label: t('Pašalinti'), handler: handleSummaryDelete },
                  { label: t('Persiųsti'), handler: () => setTransferModalOpen(true) },
                  { label: t('Archyvuoti'), handler: () => handleArchive(true) },
                  { label: t('Neleisti redaguoti'), handler: () => handleReadonly(true) }
                ] : record.isArchived ? [
                  { label: t('Iškelti iš archyvo'), handler: () => handleArchive(false) },
                  { label: t('Pašalinti'), handler: handleSummaryDelete },
                ] : [{ label: t('Leisti redagavimą'), handler: () => handleReadonly(false) }]}
              />
            </div>
          </div>
        )}
        beforeMainContent={!isTrial && showTrialMessage && renderTrialMessage()}
      >
        <div className={classes.root} style={{ paddingTop: 12 }}>
          {!!record?._id && (
            <div style={{ marginBottom: 20 }}>
              <table className="summary-debug-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th>{t('Statybos montavimo darbai')}</th>
                    <th>{t('Įrenginiai, baldai')}</th>
                    <th>{t('Kitos išlaidos')}</th>
                    <th>{t('Suma, be PVM')}</th>
                    <th>{t('PVM suma')}</th>
                    <th>{t('Suma, su PVM')}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={{ textAlign: 'right' }}>{handleValueRound(record?.constructionAmount) || '0.00'} EUR</td>
                    <td style={{ textAlign: 'right' }}>{handleValueRound(record?.equipmentAmount) || '0.00'} EUR</td>
                    <td style={{ textAlign: 'right' }}>{handleValueRound(record?.otherAmount) || '0.00'} EUR</td>
                    <td style={{ textAlign: 'right' }}>{handleValueRound(record?.totals?.priceWithoutVat) || '0.00'} EUR</td>
                    <td style={{ textAlign: 'right' }}>{handleValueRound(record?.totals?.vatAmount) || '0.00'} EUR</td>
                    <td style={{ textAlign: 'right' }}>{handleValueRound(record?.totals?.priceWithVat) || '0.00'} EUR</td>
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {!isTrial ? canAccessService ? (
            <CommonPanel>
              {isNew && (
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Field name="title" onChange={handleRecordChange} value={record?.title} label={t('Skaičiavimų objekto pavadinimas')} shrink />
                    <Typography variant="body1">
                      {t('Įveskite naujo skaičiavimų objekto pavadinimą ir išsaugokite, kad galėtumėte pradėti skaičiavimus')}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Button
                      startIcon={<SaveIcon />}
                      variant="contained"
                      color="primary"
                      onClick={() => handleSubmit()}
                      style={{ marginTop: 15 }}
                      disabled={!record?.title}
                    >
                      {t('Išsaugoti')}
                    </Button>
                  </Grid>
                </Grid>
              )}

              {!isNew && <EstimateSummaryTable
                config={config}
                summaryId={summaryId}
                rows={record?.items || []}
                onUpdate={() => fetchRecord(summaryId)}
                onObjectSearch={handleObjectSearch}
                onObjectEdit={handleObjectEdit}
                onRecoveryPriceEdit={handleRecoveryPriceEdit}
                onShowSettings={() => setSettingsModalOpen(true)}
                showRowOrderArrows={showRowOrderArrows}
                handleValueRound={handleValueRound}
                summaryIsReadonly={record?.isReadonly}
              />}
            </CommonPanel>
          ) : renderLicenceRequest() : renderTrial()}
        </div >

      </DashboardLayout >

      {settingsModalOpen && (
        <EstimateSummarySettingsModal
          rows={record?.items || []}
          summaryId={summaryId}
          onUpdate={() => fetchRecord(summaryId)}
          onClose={handleSettingsModalClose}
          summaryIsReadonly={record?.isReadonly}
        />
      )}

      {calculationSettingsModalOpen && (
        <EstimateSummaryCalculationSettingsModal
          summaryId={summaryId}
          settings={record?.settings}
          fetchRecord={fetchRecord}
          onClose={() => setCalculationSettingsModalOpen(false)}
        />
      )}

      {printModalOpen && (
        <EstimateSummaryPrintModal
          summaryId={summaryId}
          onClose={() => setPrintModalOpen(false)}
        />
      )}

      {objectModalOpenForItem && (
        <EstimateObjectImportModal
          // projectId={projectId}
          versionCode={record?.version?.code}
          versionYear={record?.version?.version.split('-')[0]}
          onClose={() => {
            setObjectModalOpenForItem(null)
            fetchRecord(summaryId)
          }}
          onSelect={(estimateObjectId, shouldMatchVersion = false, isEstimateObject, order) => handleObjectImportSelected(estimateObjectId, shouldMatchVersion, isEstimateObject, order)}
          isEstimateObject={objectModalOpenForItem.isEstimateObject}
          parent={objectModalOpenForItem?.parent}
          summaryId={summaryId}
          order={objectModalOpenForItem?.order}
          projectId={projectId}
        />
      )}

      {transferModalOpen && (
        <EstimateSummaryTransferModal
          summaryId={summaryId}
          onClose={() => setTransferModalOpen(false)}
        />
      )}
    </>
  );
};

export default EstimateSummary;